/**
 * As empresas que a tela está mostrando (D-131) — quem decide se existe a coluna Empresa.
 *
 * ## A coluna existe só em "Todas", e só com mais de uma empresa
 *
 * Num portal de uma empresa, ou com uma empresa escolhida no seletor, toda linha é da mesma
 * empresa: uma coluna repetindo o mesmo nome em cada linha seria ruído. Em "Todas", num portal
 * que reúne várias, a linha precisa dizer de qual delas ela é.
 *
 * ## O nome vem do portal, e não da linha
 *
 * A API manda só o id da empresa na linha de leitura; o nome já chegou uma vez, no `/me`, com as
 * empresas do portal. Resolver aqui é ler o que a sessão já trouxe, sem segunda consulta e sem
 * um segundo nome para a mesma empresa.
 */
import { createContext, useContext } from 'react'
import type { EmpresaDoPortalNoExterno, PortalDoExterno } from '@dv/contracts'

export interface EmpresasDaTela {
  /** "Todas" num portal com mais de uma empresa: a linha diz de qual empresa ela é. */
  readonly mostrar: boolean
  /** O nome da empresa do portal; id desconhecido vira travessão, nunca um id cru na tela. */
  readonly nomeDe: (id: string) => string
  /** "PNEUSTORE · PneuStore Digital" — as empresas de uma linha deduplicada. */
  readonly nomesDe: (ids: readonly string[]) => string
}

export function empresasDaTela(portal: PortalDoExterno, empresa: EmpresaDoPortalNoExterno | null): EmpresasDaTela {
  const nomes = new Map(portal.empresas.map((item) => [item.id, item.nome]))
  const nomeDe = (id: string): string => nomes.get(id) ?? '—'
  return {
    mostrar: empresa === null && portal.empresas.length > 1,
    nomeDe,
    nomesDe: (ids) => ids.map(nomeDe).join(' · '),
  }
}

const Contexto = createContext<EmpresasDaTela>({ mostrar: false, nomeDe: () => '—', nomesDe: () => '' })

export const ProvedorDeEmpresas = Contexto.Provider

export function useEmpresas(): EmpresasDaTela {
  return useContext(Contexto)
}
